import { useState } from "react";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { useSidebar } from "@speakai/ui/sidebar";
import { useAuth } from "@/hooks/use-auth";
import { usePermissions } from "@/hooks/use-permissions";
import { safeLocalStorage } from "@/lib/storage/safe-local-storage";
import { trialHasEnded } from "@/lib/utils/trial";
import { useActivationStatus } from "@/hooks/use-activation-status";
/* eslint-disable boundaries/dependencies */
import { useBalance } from "@/features/subscription/payment-queries";
import { useAccountUsage } from "@/features/dashboard/queries";
import { deriveCredits } from "@/features/subscription/hooks/use-credits";
import { SubscriptionPlanId, SubscriptionStatus } from "@/features/subscription/types";
import { useTeamMembers } from "@/features/team/queries";
/* eslint-enable boundaries/dependencies */

export const POWERSPOT_VARIANT_KEYS = [
  "payment_failed",
  "trial_expired",
  // "trial_expiring", "trial_active" are owned by the sidebar footer (trial card +
  // collapsed icon); the topbar does not duplicate them.
  "low_balance",
  "activation",
  "annual",
  "book_meeting",
] as const;
export type PowerspotVariantKey = (typeof POWERSPOT_VARIANT_KEYS)[number];

export interface ActivationMeta {
  completedCount: number;
  totalSteps: number;
}

const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;
const ASSIGN_TTL_MS = 24 * 60 * 60 * 1000;

const QUALIFIED_TEAM_MIN_MEMBERS = 2;
const PAYG_MIN_BALANCE_FOR_SALES_CALL = 25;
const PAYG_MIN_MEDIA_FOR_SALES_CALL = 10;
const PAYG_LOW_BALANCE_THRESHOLD = 10;
const ACTIVATION_MAX_ACCOUNT_AGE_DAYS = 14;
const ACTIVATION_MAX_MEDIA = 5;
const MS_PER_DAY = 86_400_000;

function dismissalKey(userId: string, variant: PowerspotVariantKey): string {
  return `powerspot.dismissed.${userId}.${variant}`;
}

function assignmentKey(userId: string): string {
  return `powerspot.assigned.${userId}`;
}

interface DismissRecord {
  dismissedAt: string;
  /** Optional explicit TTL in ms. When absent, the legacy DISMISS_TTL_MS constant applies. */
  durationMs?: number;
}

interface AssignRecord {
  variant: PowerspotVariantKey | "none";
  assignedAt: string;
}

function readDismissal(userId: string, variant: PowerspotVariantKey): DismissRecord | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(dismissalKey(userId, variant));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;
    const rec = parsed as DismissRecord;
    if (typeof rec.dismissedAt !== "string") return null;
    if (rec.durationMs !== undefined && typeof rec.durationMs !== "number") return null;
    return rec;
  } catch {
    return null;
  }
}

export function markDismissed(
  userId: string,
  variant: PowerspotVariantKey,
  durationDays?: number,
): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    const record: DismissRecord = { dismissedAt: new Date().toISOString() };
    if (typeof durationDays === "number" && durationDays > 0) {
      record.durationMs = durationDays * MS_PER_DAY;
    }
    ls.setItem(dismissalKey(userId, variant), JSON.stringify(record));
  } catch {
    /* quota */
  }
}

function isDismissed(userId: string, variant: PowerspotVariantKey): boolean {
  const rec = readDismissal(userId, variant);
  if (!rec) return false;
  const ttl = rec.durationMs ?? DISMISS_TTL_MS;
  const age = Date.now() - new Date(rec.dismissedAt).getTime();
  return age < ttl;
}

function readAssignment(userId: string): AssignRecord | null {
  const ls = safeLocalStorage();
  if (!ls) return null;
  try {
    const raw = ls.getItem(assignmentKey(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (typeof parsed !== "object" || parsed === null) return null;
    const rec = parsed as AssignRecord;
    if (typeof rec.variant !== "string" || typeof rec.assignedAt !== "string") return null;
    if (rec.variant !== "none" && !POWERSPOT_VARIANT_KEYS.includes(rec.variant as PowerspotVariantKey)) return null;
    if (Date.now() - new Date(rec.assignedAt).getTime() >= ASSIGN_TTL_MS) return null;
    return rec;
  } catch {
    return null;
  }
}

function writeAssignment(userId: string, variant: PowerspotVariantKey | "none"): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  try {
    ls.setItem(
      assignmentKey(userId),
      JSON.stringify({ variant, assignedAt: new Date().toISOString() } satisfies AssignRecord),
    );
  } catch {
    /* quota */
  }
}

/**
 * Removes all powerspot localStorage keys for the given user.
 * Called on logout to prevent cross-user assignment/dismissal bleed.
 */
export function clearPowerspotStorage(userId: string): void {
  const ls = safeLocalStorage();
  if (!ls) return;
  const assignedKey = assignmentKey(userId);
  const dismissedPrefix = `powerspot.dismissed.${userId}.`;
  try {
    for (let i = ls.length - 1; i >= 0; i--) {
      const k = ls.key(i);
      if (k === assignedKey || (k && k.startsWith(dismissedPrefix))) {
        try { ls.removeItem(k); } catch { /* ignore */ }
      }
    }
  } catch {
    /* ignore */
  }
}

export interface ResolverResult {
  variant: PowerspotVariantKey | null;
  loading: boolean;
  /**
   * True when the user has billing perms but no variant matches.
   * No production consumer: the top bar's own credits slot owns the balance
   * surface for trial / PAYG / credit-mode accounts. Kept only so the existing
   * resolver tests keep their shape; safe to delete with them.
   */
  showBalanceFallback: boolean;
  /**
   * Credit-mode account below the low-credit threshold.
   * No production consumer. The low-credit warning now reaches the user through
   * the always-on credits pill, which renders its own danger styling, so it no
   * longer needs to outrank the promotional pills to stay visible.
   */
  creditLow: boolean;
  /** Populated when variant === "activation" — pill consumes for progress label. */
  activationMeta?: ActivationMeta;
}

export function usePowerspotResolver(): ResolverResult {
  const { user } = useAuth();
  const { canManageBilling } = usePermissions();
  const { data: balance, isLoading: balanceLoading } = useBalance();
  const { data: usage, isLoading: usageLoading } = useAccountUsage();
  const { collapsed } = useSidebar();
  const activation = useActivationStatus();
  const bookingCtaTopbarOn = useFeatureValue("booking-cta-topbar", false);
  // pageSize:1 = minimum page; we only consume totalCount, not the rows.
  // Cache is shared with /team page so this is free after first load.
  const { data: teamData, isLoading: teamLoading } = useTeamMembers({ pageSize: 1 });
  // Capture `now` once per mount via useState lazy initializer — React purity
  // rule forbids Date.now() directly in render. Stable per session is fine for
  // a 14-day activation window (sub-second drift is irrelevant).
  const [nowMs] = useState(() => Date.now());
  const teamMemberCount = teamData?.totalCount ?? 0;
  // book_meeting is the only variant gated on team count; while teamLoading is true,
  // treat the team as not-yet-qualified so the rest of the resolver fires instantly.
  const isQualifiedTeam = !teamLoading && teamMemberCount >= QUALIFIED_TEAM_MIN_MEMBERS;

  const planId = usage?.planId;
  const isPaygPlan = planId === SubscriptionPlanId.Speak_Pay_As_You_Go;

  if (balanceLoading || usageLoading) {
    return { variant: null, loading: true, showBalanceFallback: false, creditLow: false };
  }

  // canManageBilling gates ALL variants below. Billing-adjacent variants
  // (low_balance, payment_failed, trial_expired) hard-depend on this — team
  // members cannot act on those CTAs. Non-billing variants (activation,
  // book_meeting) are also kept behind it to preserve the single gate point;
  // if a future variant needs to bypass, audit each billing-adjacent variant's
  // predicate independently before relaxing this check.
  if (!user || !canManageBilling) {
    return { variant: null, loading: false, showBalanceFallback: false, creditLow: false };
  }

  const userId = user._id;
  const durationId = usage?.durationId;
  const currentBalance = balance?.balance ?? 0;
  const totalMedia = usage?.totalMedia ?? 0;
  const trialEnded = trialHasEnded(usage?.isExpired, usage?.remainingDays);
  const isTrialExpired =
    planId === SubscriptionPlanId.Speak_Free_Trial && trialEnded;
  const isTrialActive =
    planId === SubscriptionPlanId.Speak_Free_Trial && !trialEnded;
  const isPaymentFailed =
    !isTrialActive && usage?.status === SubscriptionStatus.PendingPayment;

  const isPaidSubscription =
    planId === SubscriptionPlanId.Speak_Custom ||
    planId === SubscriptionPlanId.Speak_Build_Custom_Plan;
  const isPayg = isPaygPlan;

  const eligiblePaymentFailed =
    isPaymentFailed && !isDismissed(userId, "payment_failed");

  const eligibleTrialExpired =
    isTrialExpired && !isDismissed(userId, "trial_expired");

  const eligibleLowBalance =
    isPayg &&
    currentBalance < PAYG_LOW_BALANCE_THRESHOLD &&
    !isDismissed(userId, "low_balance");

  // Activation is for genuinely-new free-trial-style users only:
  //   - Paid subscribers (Speak_Custom / Build_Custom) skip — they're past activation
  //   - Users with ≥5 media files are past the "first upload" moment
  //   - Accounts older than 14 days are past the activation window
  // These gates prevent the pill from showing for veteran or paying users
  // who'd find a "Setup 1/4" reminder confusing or off-brand.
  const accountAgeDays = user.createdAt
    ? (nowMs - new Date(user.createdAt).getTime()) / MS_PER_DAY
    : 0;
  const accountAgeKnown = !!user.createdAt;

  const eligibleActivation =
    !activation.isLoading &&
    !activation.allComplete &&
    !activation.isDismissed &&
    !isDismissed(userId, "activation") &&
    !isPaidSubscription &&
    totalMedia < ACTIVATION_MAX_MEDIA &&
    (!accountAgeKnown || accountAgeDays < ACTIVATION_MAX_ACCOUNT_AGE_DAYS);

  const eligibleAnnual =
    planId === SubscriptionPlanId.Speak_Custom &&
    durationId === "monthly" &&
    !isDismissed(userId, "annual");

  const isQualifiedForSalesCall =
    isQualifiedTeam &&
    (planId === SubscriptionPlanId.Speak_Build_Custom_Plan ||
      isPaidSubscription ||
      (isPayg &&
        currentBalance >= PAYG_MIN_BALANCE_FOR_SALES_CALL &&
        totalMedia >= PAYG_MIN_MEDIA_FOR_SALES_CALL));

  // Mirrors BookCallButton's visibility gate exactly — qualified users get the persistent button instead of this rotation slot (never two booking CTAs); non-qualified users keep it.
  const bookCallButtonVisible = bookingCtaTopbarOn && user.salesQualified === true;
  const eligibleBookMeeting =
    isQualifiedForSalesCall && !bookCallButtonVisible && !isDismissed(userId, "book_meeting");

  // Surface-aware fallback: sidebar PAYG card owns the desktop balance surface
  // when expanded — TopBar pill only fires when sidebar is collapsed/hidden.
  // Trial users have no sidebar balance card, so pill always wins.
  const balanceFallbackVisible = isPayg ? collapsed : true;

  const { isLow: creditLow } = deriveCredits(usage);

  const activationMeta: ActivationMeta | undefined = eligibleActivation
    ? {
        completedCount: activation.completedCount,
        totalSteps: activation.totalSteps,
      }
    : undefined;

  function buildResult(variant: PowerspotVariantKey | null): ResolverResult {
    const result: ResolverResult = {
      variant,
      loading: false,
      showBalanceFallback: variant === null && balanceFallbackVisible,
      creditLow,
    };
    if (variant === "activation" && activationMeta) result.activationMeta = activationMeta;
    return result;
  }

  // Priority (highest first): payment_failed, trial_expired, annual, book_meeting,
  // activation, low_balance. Active-trial surfaces are owned by the sidebar footer.
  // Fresh resolve runs before the sticky fallback so a stale lower-priority sticky
  // never suppresses a now-eligible higher-priority variant.
  let resolved: PowerspotVariantKey | null = null;
  if (eligiblePaymentFailed) resolved = "payment_failed";
  else if (eligibleTrialExpired) resolved = "trial_expired";
  else if (eligibleAnnual) resolved = "annual";
  else if (eligibleBookMeeting) resolved = "book_meeting";
  else if (eligibleActivation) resolved = "activation";
  else if (eligibleLowBalance) resolved = "low_balance";

  // Sticky fallback: only when the fresh resolve is null, restore a still-eligible
  // prior variant so the pill does not flicker when one briefly toggles. Runs after
  // the fresh pass so it never overrides a now-eligible higher-priority variant.
  if (resolved === null) {
    const sticky = readAssignment(userId);
    if (sticky && sticky.variant !== "none") {
      if (sticky.variant === "annual" && eligibleAnnual) resolved = "annual";
      else if (sticky.variant === "book_meeting" && eligibleBookMeeting) resolved = "book_meeting";
      else if (sticky.variant === "activation" && eligibleActivation) resolved = "activation";
      else if (sticky.variant === "low_balance" && eligibleLowBalance) resolved = "low_balance";
    }
  }

  writeAssignment(userId, resolved ?? "none");
  return buildResult(resolved);
}
